import ArrowDownwardIcon from "@mui/icons-material/ArrowDownwardRounded";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpwardRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FormatIndentDecreaseIcon from "@mui/icons-material/FormatIndentDecreaseRounded";
import FormatIndentIncreaseIcon from "@mui/icons-material/FormatIndentIncreaseRounded";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import type { PropsWithChildren } from "react";
import { useCallback } from "react";
import type {
  FieldConfiguration,
  MeasureDateRange,
} from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill.tsx";
import PopoverMenuItem from "@entrilia/shared/components/PopoverMenuItem.tsx";
import type { MeasureField } from "../../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch.ts";
import type { StatementLineState } from "../../../../store/types/fieldsState.types.ts";
import { createFinancialStatementAction } from "../../../../store/utils/storeUtils.ts";
import CustomLabel from "../../../viewer/common/fields/CustomLabel.tsx";
import FieldOptionDeleteButton from "../../../viewer/common/fields/FieldOptionDeleteButton.tsx";
import FieldOptionsDivider from "../../../viewer/common/fields/FieldOptionsDivider.tsx";
import FieldPopover from "../../../viewer/common/fields/FieldPopover.tsx";
import type { GetFieldCaption } from "../../../viewer/financialStatement/statementGridModel.ts";
import AmountTypeMenuItem from "../../common/fields/field-options/AmountTypeMenuItem.tsx";
import ReverseSignMenuItem from "../../common/fields/field-options/ReverseSignMenuItem.tsx";
import SwitchMenuItem from "../../common/fields/field-options/SwitchMenuItem.tsx";
import ValueFieldsMenuItem from "../../common/fields/field-options/ValueFieldsMenuItem.tsx";
import { isGlTableMeasure, isSharesRelatedMeasure } from "../../utils/MeasureUtils.ts";
import PeriodSelect from "../common/PeriodSelect.tsx";
import { getRemoveBlockedMessage } from "../utils/statementFormulas.ts";
import {
  getLinePeriodInfo,
  getLinePeriodOptions,
  getTotalOfLinesNote,
  getTotalPickerRows,
} from "../utils/statementLineOptions.ts";
import { hasOwnPeriod, MAX_LEVEL } from "../utils/statementLines.ts";
import { getLineDefaultLabel, getStatementLineLabel } from "../utils/statementRowsArea.ts";
import TotalOfLinesPicker from "./TotalOfLinesPicker.tsx";

const REVERSE_SIGN_TOOLTIP =
  "Show the amount with its sign flipped, so a credit balance reads positive on the statement";
const GROUP_SHOW_AS_GROUP_TOOLTIP = "Show the group name as a heading above its lines";
const DIMENSION_SHOW_AS_GROUP_TOOLTIP = "Show the dimension name as a group heading above its values";
const SHOW_SUBTOTAL_TOOLTIP = "Add a subtotal row under the dimension's values";
const GRAND_TOTAL_TOOLTIP = "Draw a double underline under this total";

interface PositionButtonProps {
  title: string;
  disabled: boolean;
  onClick: () => void;
}

const PositionButton = ({ title, disabled, onClick, children }: PropsWithChildren<PositionButtonProps>) => (
  <Tooltip title={title}>
    <Box component="span">
      <IconButton disabled={disabled} onClick={onClick}>
        {children}
      </IconButton>
    </Box>
  </Tooltip>
);

interface LineFieldOptionsProps {
  field: MeasureField;
  readonly: boolean;
  saveChanges: (changes: Partial<FieldConfiguration>) => void;
}

const LineFieldOptions = ({ field, readonly, saveChanges }: LineFieldOptionsProps) => {
  const { isConfigurable, changeAmountField, changeCalculatedBy } = field.meta.capabilities;
  const isSharesMeasure = isSharesRelatedMeasure(field);

  return (
    <>
      {!isSharesMeasure && isConfigurable && changeAmountField && (
        <AmountTypeMenuItem
          measure={field}
          saveChanges={saveChanges}
          disabled={!isGlTableMeasure(field.meta) || readonly}
        />
      )}
      {!isSharesMeasure && isConfigurable && changeCalculatedBy && (
        <ValueFieldsMenuItem measure={field} saveChanges={saveChanges} readonly={readonly} />
      )}
    </>
  );
};

interface Props {
  line: StatementLineState;
  lines: StatementLineState[];
  defaultRange: MeasureDateRange;
  getFieldCaption: GetFieldCaption;
  anchorEl: HTMLElement;
  readonly: boolean;
  onEditCalculation: () => void;
  onClose: () => void;
}

const LineOptionsPopover = ({
  line,
  lines,
  defaultRange,
  getFieldCaption,
  anchorEl,
  readonly,
  onEditCalculation,
  onClose,
}: Props) => {
  const dispatch = useAppDispatch();

  const updateLine = useCallback(
    (changes: Partial<StatementLineState>) =>
      dispatch(createFinancialStatementAction("updateLine", { guid: line.guid, changes })),
    [dispatch, line.guid]
  );

  const saveFieldChanges = useCallback(
    (changes: Partial<FieldConfiguration>) =>
      dispatch(createFinancialStatementAction("updateLineField", { guid: line.guid, changes })),
    [dispatch, line.guid]
  );

  const getLabel = (candidate: StatementLineState): string => getStatementLineLabel(candidate, getFieldCaption);

  const handleToggleTotalLine = (guid: string) =>
    updateLine({
      lineIds: line.lineIds.includes(guid) ? line.lineIds.filter((id) => id !== guid) : [...line.lineIds, guid],
    });

  const handleRemove = () => {
    dispatch(createFinancialStatementAction("removeLine", line.guid));
    onClose();
  };

  const index = lines.findIndex((candidate) => candidate.guid === line.guid);
  const { lineType, field } = line;
  const isSpacer = lineType === "Spacer";
  const hasPeriod = hasOwnPeriod(lineType);
  const hasShowAsGroup = lineType === "Group" || lineType === "Dimension";
  const hasGrandTotalStyle = lineType === "Subtotal" || lineType === "Total";
  const periodInfo = getLinePeriodInfo(lines, line, defaultRange, getLabel);
  const removeBlockedMessage = getRemoveBlockedMessage(lines, line.guid, getLabel);

  return (
    <FieldPopover open anchorEl={anchorEl} onClose={onClose} sx={{ width: 400, p: 0 }}>
      {!isSpacer && (
        <>
          <CustomLabel
            customFieldValue={line.customLabel}
            originalFieldValue={getLineDefaultLabel(line, getFieldCaption)}
            updateCustomFieldValue={(customLabel) => updateLine({ customLabel })}
            readonly={readonly}
          />
          <FieldOptionsDivider />
        </>
      )}
      <Stack direction="row" sx={{ alignItems: "center", gap: 0.5, px: 1.5, py: 0.75 }}>
        <PositionButton
          title="Move Up"
          disabled={readonly || index <= 0}
          onClick={() => dispatch(createFinancialStatementAction("moveLine", { guid: line.guid, direction: "up" }))}
        >
          <ArrowUpwardIcon />
        </PositionButton>
        <PositionButton
          title="Move Down"
          disabled={readonly || index === -1 || index >= lines.length - 1}
          onClick={() => dispatch(createFinancialStatementAction("moveLine", { guid: line.guid, direction: "down" }))}
        >
          <ArrowDownwardIcon />
        </PositionButton>
        {!isSpacer && (
          <>
            <PositionButton
              title="Outdent"
              disabled={readonly || line.level === 0}
              onClick={() => dispatch(createFinancialStatementAction("outdentLine", line.guid))}
            >
              <FormatIndentDecreaseIcon />
            </PositionButton>
            <PositionButton
              title="Indent"
              disabled={readonly || line.level >= MAX_LEVEL}
              onClick={() => dispatch(createFinancialStatementAction("indentLine", line.guid))}
            >
              <FormatIndentIncreaseIcon />
            </PositionButton>
            <HorizontalFill />
            <Typography variant="caption" color="textSecondary" sx={{ pr: 0.5 }}>
              {`Level ${line.level + 1}`}
            </Typography>
          </>
        )}
      </Stack>
      <FieldOptionsDivider />
      {lineType === "Formula" && !readonly && (
        <>
          <PopoverMenuItem sx={{ gap: 1 }} onClick={onEditCalculation}>
            <EditOutlinedIcon color="secondary" />
            <Typography>Edit Calculation...</Typography>
          </PopoverMenuItem>
          <FieldOptionsDivider />
        </>
      )}
      {hasPeriod && (
        <>
          <Stack spacing={1.5} useFlexGap sx={{ px: 2, py: 1.5 }}>
            <PeriodSelect
              dateRange={line.dateRange}
              options={getLinePeriodOptions(periodInfo.inheritedRange)}
              note={periodInfo.note}
              readonly={readonly}
              onChange={(dateRange) => updateLine({ dateRange })}
            />
            {field && <LineFieldOptions field={field} readonly={readonly} saveChanges={saveFieldChanges} />}
          </Stack>
          {field && (
            <ReverseSignMenuItem
              field={field}
              saveChanges={saveFieldChanges}
              readonly={readonly}
              tooltip={REVERSE_SIGN_TOOLTIP}
            />
          )}
          <FieldOptionsDivider />
        </>
      )}
      {hasShowAsGroup && (
        <>
          <SwitchMenuItem
            caption="Show as Group"
            tooltip={lineType === "Group" ? GROUP_SHOW_AS_GROUP_TOOLTIP : DIMENSION_SHOW_AS_GROUP_TOOLTIP}
            value={line.showAsGroup}
            onClick={() => updateLine({ showAsGroup: !line.showAsGroup })}
            disabled={readonly}
          />
          {lineType === "Dimension" && (
            <SwitchMenuItem
              caption="Show Subtotal"
              tooltip={SHOW_SUBTOTAL_TOOLTIP}
              value={line.showSubtotal}
              onClick={() => updateLine({ showSubtotal: !line.showSubtotal })}
              disabled={readonly}
            />
          )}
          <FieldOptionsDivider />
        </>
      )}
      {lineType === "Total" && (
        <>
          <TotalOfLinesPicker
            rows={getTotalPickerRows(lines, line, getLabel)}
            note={getTotalOfLinesNote(line.lineIds.length)}
            readonly={readonly}
            onToggle={handleToggleTotalLine}
          />
          <FieldOptionsDivider />
        </>
      )}
      {hasGrandTotalStyle && (
        <>
          <SwitchMenuItem
            caption="Grand Total Styling"
            tooltip={GRAND_TOTAL_TOOLTIP}
            value={line.grandTotalStyle}
            onClick={() => updateLine({ grandTotalStyle: !line.grandTotalStyle })}
            disabled={readonly}
          />
          <FieldOptionsDivider />
        </>
      )}
      {!readonly && (
        <Tooltip title={removeBlockedMessage}>
          <Box>
            <FieldOptionDeleteButton disabled={removeBlockedMessage !== undefined} onRemove={handleRemove}>
              Remove Line
            </FieldOptionDeleteButton>
          </Box>
        </Tooltip>
      )}
    </FieldPopover>
  );
};

export default LineOptionsPopover;
